import { fail, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { blaetterMitFilter, filtertGerade, readData } from "@/office/excel/daten";
import { nutztFunktion } from "@/office/excel/formeln";
import { allTables, formulaCells, readWorkbook } from "@/office/excel/workbook";
import { nichtPruefbar } from "../schnittstelle";

export const lektionFiltern: Lesson = {
  id: "filtern",
  title: "Filtern: die Daten bleiben, die Sicht ändert sich",
  summary: "Ein Filter löscht nichts – er blendet aus. Was das für deine Auswertungen bedeutet.",
  durationMinutes: 11,
  learningGoals: [
    "Einen AutoFilter setzen und mehrere Kriterien kombinieren",
    "Den Unterschied zwischen Ausblenden und Filtern kennen",
    "Mit TEILERGEBNIS eine mitlaufende Auswertung bauen",
  ],
  steps: [
    {
      id: "filter-setzen",
      title: "Filterschaltflächen und was dahintersteckt",
      explanation: [
        "Ein Filter blendet Zeilen aus, die nicht zu einem Kriterium passen. Die Daten " +
          "bleiben vollständig erhalten – man sieht nur einen Ausschnitt. Erkennbar ist " +
          "das an zwei Stellen: Die Zeilennummern springen, und die Filterschaltfläche " +
          "der gefilterten Spalte zeigt ein kleines Trichtersymbol.",
        "In einer Tabelle sind die Schaltflächen schon da. Für einen losen Bereich " +
          "setzt man sie mit Strg + Umschalt + L oder über „Daten“ → „Filtern“; " +
          "dieselbe Kombination schaltet sie auch wieder aus und hebt dabei alle Filter " +
          "auf.",
        "Filtern lässt sich nach Werten – über die Häkchenliste – und nach Regeln. " +
          "Welche Regeln angeboten werden, hängt vom Inhalt der Spalte ab: " +
          "Zahlenfilter mit „größer als“ und „zwischen“, Textfilter mit „beginnt mit“ " +
          "und „enthält“, Datumsfilter mit „letzter Monat“ und „dieses Jahr“. Bietet " +
          "Excel Textfilter an, wo Zahlen stehen sollten, ist das ein sicherer Hinweis " +
          "auf die Textfalle aus Modul 2.",
        "Mehrere Spalten gleichzeitig zu filtern, verknüpft die Kriterien mit UND: " +
          "Region Nord und Betrag über 1000. Ein ODER über verschiedene Spalten geht " +
          "damit nicht – dafür braucht es eine Hilfsspalte mit einer Formel, oder man " +
          "wechselt gleich zur PivotTable aus dem Aufbauteil.",
      ],
      keyPoints: [
        "Strg + Umschalt + L schaltet die Filterschaltflächen ein und aus.",
        "Springende Zeilennummern und ein Trichtersymbol zeigen einen aktiven Filter.",
        "Mehrere Spalten filtern verknüpft mit UND, nie mit ODER.",
      ],
      task: {
        instruction:
          "Setze einen Filter auf deine Daten und wähle ein Kriterium, sodass " +
          "tatsächlich Zeilen ausgeblendet werden.",
        hint: "Filterschaltfläche in der Kopfzeile anklicken und ein paar Häkchen entfernen. Die Zeilennummern müssen danach springen.",
        check: async () => {
          const daten = await readData();
          const mappe = await readWorkbook();
          const tabellen = allTables(mappe);

          const tabelleGefiltert = tabellen.some((tabelle) => tabelle.isFiltered === true);
          const tabellenLesbar = tabellen.some((tabelle) => tabelle.isFiltered !== null);

          if (daten.autoFilters === null && !tabellenLesbar) {
            return nichtPruefbar(
              "Der Filterzustand eines Blattes",
              "1.9",
              "Sieh nach, ob die Zeilennummern springen und die Filterschaltfläche ein Trichtersymbol zeigt.",
            );
          }

          if (filtertGerade(daten) || tabelleGefiltert) {
            const blatt = blaetterMitFilter(daten).find((eintrag) => eintrag.isDataFiltered === true);
            return pass(
              blatt
                ? `Auf „${blatt.sheetName}“ ist ein Filter über ${blatt.address ?? "die Daten"} aktiv – ` +
                  "es werden gerade Zeilen ausgeblendet."
                : "In deiner Tabelle ist gerade ein Filter aktiv – es werden Zeilen ausgeblendet.",
            );
          }

          const gesetzt = blaetterMitFilter(daten);
          if (gesetzt.length > 0 || tabellen.length > 0) {
            return fail(
              "Die Filterschaltflächen sind da, aber es ist noch kein Kriterium ausgewählt – " +
                "es wird also nichts ausgeblendet.",
              "Klicke eine Filterschaltfläche in der Kopfzeile an und entferne ein paar Häkchen aus " +
                "der Werteliste.",
            );
          }

          return fail(
            "Auf keinem Blatt sind Filterschaltflächen gesetzt.",
            "Setze den Cursor in deine Daten und drücke Strg + Umschalt + L.",
          );
        },
      },
    },
    {
      id: "teilergebnis",
      title: "TEILERGEBNIS rechnet nur das Sichtbare",
      explanation: [
        "Und jetzt die Falle, die im Tabellenmodul schon angeklungen ist: Eine SUMME " +
          "unter gefilterten Daten addiert weiterhin alles. Auch die ausgeblendeten " +
          "Zeilen. Das ist konsequent – der Filter ändert nur die Sicht, nicht die " +
          "Daten – und trotzdem regelmäßig eine böse Überraschung, wenn jemand die " +
          "Zahl aus einer gefilterten Ansicht abschreibt.",
        "TEILERGEBNIS macht es anders. Die Funktion bekommt als erstes Argument eine " +
          "Nummer, die sagt, was gerechnet werden soll: 9 für Summe, 1 für Mittelwert, " +
          "2 für Anzahl, 3 für Anzahl2, 4 für Maximum, 5 für Minimum. " +
          "=TEILERGEBNIS(9;D2:D100) summiert nur die sichtbaren Zeilen und rechnet sich " +
          "bei jedem Filterwechsel neu.",
        "Es gibt jede dieser Nummern zweimal: 9 und 109 bedeuten beide Summe. Der " +
          "Unterschied liegt bei den von Hand ausgeblendeten Zeilen. Die 100er-Variante " +
          "ignoriert auch diese, die einstellige nur die weggefilterten. Für die " +
          "meisten Zwecke ist 109 die richtige Wahl, weil sie das tut, was man " +
          "erwartet: alles Unsichtbare weglassen.",
        "Ein angenehmer Nebeneffekt: TEILERGEBNIS zählt andere TEILERGEBNIS-Zellen " +
          "innerhalb seines Bereichs nicht mit. Man kann also Zwischensummen in eine " +
          "Liste setzen und darunter eine Gesamtsumme bilden, ohne dass etwas doppelt " +
          "gezählt wird. Genau das nutzt die Ergebniszeile einer Tabelle.",
      ],
      keyPoints: [
        "=TEILERGEBNIS(9;Bereich) summiert nur sichtbare Zeilen.",
        "109 statt 9 ignoriert auch von Hand ausgeblendete Zeilen.",
        "Die Ergebniszeile einer Tabelle setzt genau diese Funktion ein.",
      ],
      task: {
        instruction:
          "Baue eine Auswertung mit TEILERGEBNIS und prüfe, dass sie sich beim " +
          "Filterwechsel mitändert.",
        hint: "=TEILERGEBNIS(109;D2:D100) – oder schalte einfach die Ergebniszeile deiner Tabelle ein, dort steht sie schon.",
        check: async () => {
          const mappe = await readWorkbook();
          if (!mappe.features.cells) {
            return nichtPruefbar(
              "Die Formeln der Zellen",
              "1.4",
              "Sieh in der Bearbeitungsleiste nach, ob dort TEILERGEBNIS steht.",
              true,
            );
          }

          const teilergebnisse = formulaCells(mappe).filter((zelle) =>
            nutztFunktion(zelle.formula, "teilergebnis"),
          );

          if (teilergebnisse.length === 0) {
            const mitErgebniszeile = allTables(mappe).some((tabelle) => tabelle.showTotals === true);
            return fail(
              "Es steht noch keine Formel mit TEILERGEBNIS in der Arbeitsmappe.",
              mitErgebniszeile
                ? "Deine Tabelle hat eine Ergebniszeile – dort steht TEILERGEBNIS. Klicke in eine ihrer " +
                  "Zellen und wähle über den Auswahlpfeil eine Funktion aus, dann ist die Formel da."
                : "Schreibe unter deine Daten =TEILERGEBNIS(109;D2:D100) und passe den Bereich an. " +
                  "Oder schalte die Ergebniszeile deiner Tabelle ein.",
            );
          }

          const erste = teilergebnisse[0];
          return pass(
            `In ${erste.address} steht „${erste.formula}“ und liefert gerade „${erste.text}“. ` +
              "Ändere den Filter – der Wert rechnet sich mit.",
          );
        },
      },
    },
  ],
};
